/**
 * SAP CONFIDENTIAL 
 *
 * This software is confidential proprietary software licensed by SAP SE 
 * and is not to be used, duplicated or disclosed to anyone without the 
 * prior written permission of SAP. 
 *
 * (C) 2020 Copyright SAP SE.
 */
import { Component, OnInit, ElementRef, OnDestroy, NgZone, ViewChild, HostListener } from '@angular/core';
import { Router, Event, NavigationStart, NavigationEnd, NavigationError } from '@angular/router';
import { LoginService } from '../../components/login/login.service';
import { CommonService } from '../../shared/services/common.service';
import screenfull from 'screenfull';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { eventSubscriber } from '../../shared/common-child.interface';
import { trigger, state, animate, transition, style } from '@angular/animations';
import { DistributorSearchComponent } from '../distributor-search/distributor-search.component';

@Component({
  selector: 'header-comp',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateX(0)',
        'visibility': 'visible'
      })),
      state('out', style({
        transform: 'translateX(100%)',
        'visibility': 'hidden'
      })),
      transition('in => out', animate('400ms ease-in-out')),
      transition('out => in', animate('400ms ease-in-out'))
    ])
  ]
})

export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild(DistributorSearchComponent)distributorSearch!: DistributorSearchComponent;
  public userName = '';
  public userLogo = '';
  public userGroups: any;
  public user: any = {};
  public fullScreenEnabled = false;
  public languages: any;
  public selLang: any;
  private subscription!: Subscription;
  public panelState: any = false;
  public headerState: any = false;
  public appRoutes: any = '';
  public showFullSearchbar = false;
  // MLM-2503
  public helpObj:any = {};
  public showHelpSlide = 'out';
  public showbookmarksSlide = 'out';
  public showRecentlyViewedSlide = 'out';
  public isformuser!: boolean;

  constructor(private loginService: LoginService,
    private router: Router,
    private commonService: CommonService,
    private location: Location,
    private zone: NgZone,
    private elementRef: ElementRef) {
    this.router.events.subscribe((event: Event) => {

      this.closeHelpSlider = this.closeHelpSlider.bind(this);
      eventSubscriber(commonService.subscription, this.closeHelpSlider);

      this.closeBookmarkSlider = this.closeBookmarkSlider.bind(this);
      eventSubscriber(commonService.subscription, this.closeBookmarkSlider);

      this.closeRecentlyViewedSlider = this.closeRecentlyViewedSlider.bind(this);
      eventSubscriber(commonService.subscription, this.closeRecentlyViewedSlider);

      if (event instanceof NavigationStart) {
        this.appRoutes = event.url;
      }

      if (event instanceof NavigationEnd) {
        // Hide loading indicator
      }

      if (event instanceof NavigationError) {
        // Hide loading indicator
        // Present error to user
        console.log(event.error);
      }
    });
  }

  ngOnInit() {
    if (screenfull.isEnabled) {
      screenfull.on('change', this.fullScreenChangeHandler.bind(this));
    }
    this.getUserDetails();
    // this.helpContent = this.commonService.getHelpObj();
    this.appRoutes = this.location.path();
    this.languages = [{ 'id': 'en', 'name': 'English' }, { 'id': 'fr', 'name': 'French' }, { 'id': 'po', 'name': 'Portuguese' }, { 'id': 'sp', 'name': 'Spanish' }];
    this.selLang = 'en';
    this.subscription = this.commonService.getPanelState().subscribe(panelState => {
      this.panelState = panelState;
      if (panelState !== 'hide') {
        this.headerState = panelState;
      }
    });
    this.commonService.getGenealogyConfig()
      .then(this.getGenealogyConfigSH.bind(this), (reason) =>this.commonService.commonErrorHandler(reason));
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    if (this.distributorSearch && !this.distributorSearch.elementRef.nativeElement.contains(targetElement)) {
      this.resetDistributorSearch();
    }
  }

  // Reset the distributor search component when needed
  public resetDistributorSearch() {
    if (this.distributorSearch) {
      this.distributorSearch.clearFilter();
    }
  }

  public getTranslationWithKey(key: string) {
    return this.commonService.getTranslatedVal(key);
  }

  private getGenealogyConfigSH(data: any) {
    const response = data;
    if (response) {
      const result = response;
      this.commonService.setGenealogyConfiguration(result.leadershipFunction);
      this.commonService.setCompanyConfiguration(result);
    } else {
      this.commonService.setGenealogyConfiguration({ name: '' });
    }
  }

  ngOnDestroy() {
    // unsubscribe to ensure no memory leaks
    this.subscription.unsubscribe();
    eventSubscriber(this.commonService.subscription, this.closeHelpSlider, true);

    this.subscription.unsubscribe();
    eventSubscriber(this.commonService.subscription, this.closeBookmarkSlider, true);

    this.subscription.unsubscribe();
    eventSubscriber(this.commonService.subscription, this.closeRecentlyViewedSlider, true);
    if (screenfull.isEnabled) {
      screenfull.off('change', this.fullScreenChangeHandler.bind(this));
    }
  }

  public toggleFullScreen() {
    if (!screenfull.isFullscreen) {
      this.fullScreenEnabled = true;
    } else {
      this.fullScreenEnabled = false;
    }
    if (screenfull.isEnabled) {
      screenfull.toggle();
    }
  }

  public fullScreenChangeHandler() {
    this.fullScreenEnabled = screenfull.isFullscreen;
  }

  public toggleSidePanel() {
    if (this.panelState !== 'hide') {
      this.commonService.sendPanelState('hide');
    } else {
      this.commonService.sendPanelState(this.headerState);
    }
  }

  private getUserDetails() {
    let userInfo: any = {};
    userInfo = this.commonService.getUserDetails();
    if (!userInfo) {
      this.loginService.getUserProfileDetails()
        .then(this.getUserDetailsSH.bind(this), (reason) =>this.commonService.commonErrorHandler(reason));
    } else {
      userInfo = this.commonService.getUserDetails();
      this.getUserDetailsSH(userInfo);
    }
  }

  private getUserDetailsSH(response: any) {
    const responseMsg = response;
    if (responseMsg) {
      this.loginService.setUser(responseMsg);
      this.commonService.setUserDetails(responseMsg);
      const localeCurrency = responseMsg.region?.currency;
      const localeCode = responseMsg.region?.currencyFormat;
      this.commonService.setLocale(localeCode,localeCurrency);
      this.user = responseMsg;
      this.userName = this.prepareUserName(this.user);
      this.userGroups = this.prepareUserGroups(this.user);
	  this.isformuser = responseMsg.formBaseLogin;
    }
  }

  private prepareUserName(user: any) {
    this.userLogo = '';
    const names = [],
      firstName = user ? (user.firstName ? user.firstName : '') : '';
    if (firstName) {
      names.push(firstName);
      this.userLogo = firstName.toString()[0].toUpperCase();
    }
    const lastName = user ? (user.lastName ? user.lastName : '') : '';
    if (lastName) {
      names.push(lastName);
      this.userLogo += lastName.toString()[0].toUpperCase();
    }
    return names.join(' ');
  }

  private prepareUserGroups(user: any) {
    const groups = [];
    if (user.groups && user.groups.length) {
      for (const grp of user.groups) {
        groups.push(grp.name);
      }
    }
    return groups.join(', ');
  }

  public navigateToProfilePage() {
    this.zone.run(() => {
    this.router.navigateByUrl('/profile/id');
  });
  }

  public logout() {
    /* Clear out all local storage caches */
    const userLanguage: any = localStorage.getItem('userLanguage');
    localStorage.clear();
    localStorage.setItem('systemLogout', 'true');
    localStorage.setItem('userLanguage', userLanguage);
    this.loginService.authenticateLogOutDetails()
      .then(this.getLogoutDetailsSH.bind(this), this.commonLogOutErrorHandler.bind(this));
  }

  private getLogoutDetailsSH(response: any) {
    const responseMsg = response;
    if (!responseMsg.success) {
      this.loginService.setUser('');
      this.zone.run(() => {
      this.router.navigateByUrl('\login');
    });
    }
  }

  private commonLogOutErrorHandler(error: any) {
    if (error.status == 401) {
      this.loginService.setUser('');
      this.zone.run(() => {
      this.router.navigateByUrl('\login');
    });
    }
  }

  public microsoftLogout(){
     /* Clear out all local storage caches */
     const userLanguage: any = localStorage.getItem('userLanguage');
     localStorage.clear();
     localStorage.setItem('systemLogout', 'true');
     localStorage.setItem('userLanguage', userLanguage);
     const location = window.location,
       hostName = location.hostname,
       protocol = location.protocol,
       port = location.port;
       if (hostName === 'localhost' || hostName === '127.0.0.1') {
       /* For local development */
         window.location.href = protocol + '//' + hostName + ':8084' + '/logout?userType=mfa';
       } else {
       /*  - For hosted env with ip address
        *   - port is defaulted to 80 incase not available
        */
       window.location.href = protocol + '//' + hostName + ':' + (port || '') + '/api/logout?userType=mfa';
     }
   }

  public onSearchedMemberSelected(member: any) {
    this.showFullSearchbar = false;
  }

  public displayHelpPanel(){
    const helpObj = this.commonService.getHelpObj();
    this.helpObj = helpObj? JSON.parse(JSON.stringify(helpObj)): {};
    this.toggleHelpSlide();
  }

  public displayBookmarkPanel(){
    this.toggleBookmarkSlider();
  }

  public displayRecentPanel(){
    this.toggleRecentlyViewedSlider();
  }

  public closeSearch(evt: boolean) {
    this.showFullSearchbar = evt;
  }

  private closeHelpSlider() {
    this.showHelpSlide = 'out';
  }

  private closeBookmarkSlider(){
    this.showbookmarksSlide = 'out';
  }

  private closeRecentlyViewedSlider(){
    this.showRecentlyViewedSlide = 'out';
  }

  public toggleHelpSlide(event?: any) {
    if (!event) {
      this.showHelpSlide = (this.showHelpSlide === 'in' ? 'out' : 'in');
    } else {
      this.showHelpSlide = 'out';
    }
  }

  public toggleBookmarkSlider(event?: any){
    if (!event) {
      this.showbookmarksSlide = (this.showbookmarksSlide === 'in' ? 'out' : 'in');
    } else {
      this.showbookmarksSlide = 'out';
    }
  }

  public toggleRecentlyViewedSlider(event?: any){
    if (!event) {
      this.showRecentlyViewedSlide = (this.showRecentlyViewedSlide === 'in' ? 'out' : 'in');
    } else {
      this.showRecentlyViewedSlide = 'out';
    }
  }
}
